import React from 'react';
import ProfileAssistantRow from '../../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../../GeneralProfile/ProfileUserAnswerRow';
import { shouldShowStepInHistory, stepQuestionKey } from './employeeSectionSteps';
import type { EmployeeSectionFlow } from './useEmployeeSectionFlow';
import type { EmployeeSectionStep } from './employeeSectionTypes';

function AnswerRow({
  flow,
  fieldStep,
  showUndo,
  children,
}: {
  flow: EmployeeSectionFlow;
  fieldStep: EmployeeSectionStep;
  showUndo: boolean;
  children: React.ReactNode;
}) {
  const showFieldEdit = flow.reviewMode && !flow.editingFromReview;
  return (
    <ProfileUserAnswerRow
      showUndo={!showFieldEdit && showUndo}
      onUndo={() => void flow.handleUndo()}
      undoLabel={flow.undoLabel}
      showEdit={showFieldEdit}
      onEdit={() => void flow.beginEditField(fieldStep)}
      editLabel={flow.editLabel}
    >
      {children}
    </ProfileUserAnswerRow>
  );
}

export default function EmployeeSectionChatThread({ flow }: { flow: EmployeeSectionFlow }) {
  const { t } = flow;

  return (
    <>
      {flow.fullNameCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('fullName'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="fullName" showUndo={flow.showUndoFullName}>
            {flow.userAnswerForStep('fullName')}
          </AnswerRow>
        </>
      )}
      {flow.fullNameKanaCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('fullNameKana'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="fullNameKana" showUndo={flow.showUndoFullNameKana}>
            {flow.userAnswerForStep('fullNameKana')}
          </AnswerRow>
        </>
      )}
      {flow.dobCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('dob'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="dob" showUndo={flow.showUndoDob}>
            {flow.userAnswerForStep('dob')}
          </AnswerRow>
        </>
      )}
      {flow.myNumberCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('myNumber'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="myNumber" showUndo={flow.showUndoMyNumber}>
            {flow.userAnswerForStep('myNumber')}
          </AnswerRow>
        </>
      )}
      {flow.addressCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('address'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="address" showUndo={flow.showUndoAddress}>
            <span style={{ whiteSpace: 'pre-line' }}>{flow.userAnswerForStep('address')}</span>
          </AnswerRow>
        </>
      )}
      {flow.headOfHouseholdCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('headOfHousehold'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="headOfHousehold" showUndo={flow.showUndoHeadOfHousehold}>
            {flow.userAnswerForStep('headOfHousehold')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory('headName', flow.headNameCommitted, flow.decl) && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('headName'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="headName" showUndo={flow.showUndoHeadName}>
            {flow.userAnswerForStep('headName')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory('headRelationship', flow.headRelationshipCommitted, flow.decl) && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('headRelationship'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="headRelationship" showUndo={flow.showUndoHeadRelationship}>
            {flow.userAnswerForStep('headRelationship')}
          </AnswerRow>
        </>
      )}
      {flow.employeeIncomeCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('employeeEstimatedIncome'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="employeeEstimatedIncome" showUndo={flow.showUndoEmployeeIncome}>
            {flow.userAnswerForStep('employeeEstimatedIncome')}
          </AnswerRow>
        </>
      )}
      {flow.multipleEmployersCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('multipleEmployers'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="multipleEmployers" showUndo={flow.showUndoMultipleEmployers}>
            {flow.userAnswerForStep('multipleEmployers')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory('multipleEmployersNotice', flow.noticeAcknowledged, flow.decl) &&
        flow.multipleEmployersCommitted && (
        <>
          <ProfileAssistantRow>
            {t('submitDocuments.dependentDeduction.employee.multipleEmployersNotice')}
          </ProfileAssistantRow>
          {flow.reviewMode && flow.noticeAcknowledged && (
            <AnswerRow flow={flow} fieldStep="multipleEmployers" showUndo={false}>
              {flow.userAnswerForStep('multipleEmployersNotice')}
            </AnswerRow>
          )}
        </>
      )}
      {!flow.isComplete && !flow.reviewMode && !flow.editingFromReview && (
        <ProfileAssistantRow>
          {flow.step === 'multipleEmployersNotice'
            ? t('submitDocuments.dependentDeduction.employee.multipleEmployersNotice')
            : t(stepQuestionKey(flow.step))}
        </ProfileAssistantRow>
      )}
    </>
  );
}
